import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_c07ebe4f-8fac-4375-bd77-db80e53251b9/artifacts/3hq7a21m_flinch-logo-primary-dark.png";

const Footer = () => {
  const { isDarkMode } = useTheme();
  
  const bgClass = isDarkMode ? 'bg-[#0A0A0A] border-t border-white/5' : 'bg-[#FAFAFA] border-t border-[#E5E5E5]';
  const linkClass = isDarkMode 
    ? 'text-sm text-[#555555] hover:text-[#F2F2F2] transition-colors' 
    : 'text-sm text-[#555555] hover:text-[#1A1A1A] transition-colors';
  const copyrightClass = isDarkMode ? 'text-sm text-[#444444]' : 'text-sm text-[#555555]';
  const iconClass = isDarkMode
    ? 'text-[#555555] hover:text-[#F6CD17] transition-colors'
    : 'text-[#555555] hover:text-[#F6CD17] transition-colors';

  const socialLinks = [
    {
      name: 'LinkedIn',
      url: 'https://www.linkedin.com/company/weareflinch/',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      )
    },
    {
      name: 'Instagram',
      url: 'https://www.instagram.com/we_are_flinch/',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/>
        </svg>
      )
    }
  ];

  return (
    <footer className={`py-8 px-6 md:px-8 ${bgClass} transition-colors duration-300`}>
      <div className="max-w-6xl mx-auto">
        {/* Main Footer Content */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          {/* Logo */}
          <Link to="/" className="transition-opacity duration-300 hover:opacity-80">
            <img 
              src={LOGO_URL} 
              alt="FLINCH Logo - Strategic Marketing Partner" 
              className={`h-5 w-auto ${isDarkMode ? 'brightness-0 invert' : ''}`}
            />
          </Link>
          
          {/* Navigation Links */}
          <div className="flex gap-6">
            <Link to="/" className={linkClass}>
              Home
            </Link>
            <Link to="/shift" className={linkClass}>
              The Shift
            </Link>
            <Link to="/why-us" className={linkClass}>
              Why Us?
            </Link>
            <Link to="/insights" className={linkClass}>
              Insights
            </Link>
          </div>
          
          {/* Social Media Icons */}
          <div className="flex items-center gap-4">
            {socialLinks.map((social) => (
              <a
                key={social.name}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className={iconClass}
                aria-label={`Follow FLINCH on ${social.name}`}
                data-testid={`social-${social.name.toLowerCase()}`}
              >
                {social.icon}
              </a>
            ))}
          </div>
        </div>
        
        {/* Copyright & Contact */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 mt-6 pt-6 border-t border-inherit">
          <span className={copyrightClass}>© 2025 FLINCH</span>
          <a 
            href="mailto:contact@weareflinch.com" 
            className={`${linkClass} hover:text-[#F6CD17]`}
          >
            contact@weareflinch.com
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
